<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import pipelineSequenceDefinitionsAPI from 'dashboard/api/pipelineSequenceDefinitions';

const props = defineProps({
  show: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();
const baseKey = 'PIPELINE.SEQUENCE_DEFINITIONS.WEBHOOK_CREDENTIALS';
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -- all suffixes are fixed catalog keys local to this modal
const tr = (key, values) => t(baseKey + '.' + key, values);
const legacyInboundCanonicalPayload = 'raw_body';
const v2InboundCanonicalPayload = 'timestamp.nonce.raw_body';
const outboundCanonicalPayload = 'raw_body';

const credentials = ref(null);
const isLoading = ref(false);
const loadError = ref(false);
const isRotating = ref(false);
const isConfirmingRotation = ref(false);
const showInboundSecret = ref(false);
const showOutboundSecret = ref(false);
const rotationConfirmation = ref(null);
let loadRequestId = 0;
let rotationOperationId = 0;

const accountId = computed(() => store.getters.getCurrentAccountId);

const hasAnyCredential = computed(
  () =>
    credentials.value?.inbound_configured ||
    credentials.value?.outbound_configured
);

const usesInboundV2 = computed(
  () => credentials.value?.inbound_signature_version === 2
);

const clearSensitiveState = () => {
  loadRequestId += 1;
  rotationOperationId += 1;
  credentials.value = null;
  loadError.value = false;
  isLoading.value = false;
  isRotating.value = false;
  isConfirmingRotation.value = false;
  showInboundSecret.value = false;
  showOutboundSecret.value = false;
};

const close = () => {
  clearSensitiveState();
  emit('close');
};

const loadCredentials = async () => {
  const requestedAccountId = accountId.value;
  if (!requestedAccountId || !props.show) return;
  const requestId = loadRequestId + 1;
  loadRequestId = requestId;
  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } =
      await pipelineSequenceDefinitionsAPI.webhookCredentials(
        requestedAccountId
      );
    if (
      requestId === loadRequestId &&
      props.show &&
      accountId.value === requestedAccountId
    ) {
      credentials.value = data;
    }
  } catch {
    if (
      requestId === loadRequestId &&
      props.show &&
      accountId.value === requestedAccountId
    ) {
      loadError.value = true;
    }
  } finally {
    if (requestId === loadRequestId && accountId.value === requestedAccountId) {
      isLoading.value = false;
    }
  }
};

const rotateCredentials = async credentialScope => {
  if (isConfirmingRotation.value || isRotating.value) return;
  const requestedAccountId = accountId.value;
  if (!requestedAccountId || !props.show) return;
  const operationId = rotationOperationId + 1;
  rotationOperationId = operationId;
  isConfirmingRotation.value = true;
  let confirmed = false;
  try {
    confirmed = await rotationConfirmation.value?.showConfirmation();
  } finally {
    if (operationId === rotationOperationId) {
      isConfirmingRotation.value = false;
    }
  }
  if (operationId !== rotationOperationId) return;
  if (!confirmed || !props.show || accountId.value !== requestedAccountId) {
    return;
  }

  isRotating.value = true;
  try {
    const { data } =
      await pipelineSequenceDefinitionsAPI.rotateWebhookCredentials(
        credentialScope,
        requestedAccountId
      );
    if (
      operationId !== rotationOperationId ||
      !props.show ||
      accountId.value !== requestedAccountId
    ) {
      return;
    }

    credentials.value = data;
    showInboundSecret.value = false;
    showOutboundSecret.value = false;
    showAlert(tr('ROTATE_SUCCESS'));
  } catch {
    if (
      operationId === rotationOperationId &&
      props.show &&
      accountId.value === requestedAccountId
    ) {
      showAlert(tr('ROTATE_ERROR'));
    }
  } finally {
    if (operationId === rotationOperationId) isRotating.value = false;
  }
};

const copyCredential = async value => {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    showAlert(tr('COPY_SUCCESS'));
  } catch {
    showAlert(tr('COPY_ERROR'));
  }
};

watch(
  [() => props.show, accountId],
  ([visible, currentAccountId]) => {
    clearSensitiveState();
    if (visible && currentAccountId) loadCredentials();
  },
  { immediate: true }
);

onBeforeUnmount(clearSensitiveState);
</script>

<template>
  <Modal
    :show="show"
    size="!max-w-[calc(100vw-2rem)] sm:!max-w-3xl"
    :aria-label="tr('TITLE')"
    @close="close"
  >
    <div class="flex max-h-[85vh] flex-col">
      <header class="border-b border-n-weak py-5 pl-6 pr-14">
        <h2 data-modal-title class="text-lg font-semibold text-n-slate-12">
          {{ tr('TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">{{ tr('DESCRIPTION') }}</p>
      </header>

      <div class="overflow-y-auto px-6 py-5">
        <div
          v-if="isLoading"
          class="flex min-h-40 items-center justify-center gap-2 text-sm text-n-slate-10"
          role="status"
        >
          <span
            class="size-4 animate-spin rounded-full border-2 border-n-alpha-2 border-t-n-brand motion-reduce:animate-none"
          />
          {{ tr('LOADING') }}
        </div>

        <div
          v-else-if="loadError"
          class="flex min-h-40 flex-col items-center justify-center gap-3 text-center"
          role="alert"
        >
          <p class="text-sm text-n-ruby-11">{{ tr('LOAD_ERROR') }}</p>
          <button
            type="button"
            class="min-h-11 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="loadCredentials"
          >
            {{ tr('RETRY') }}
          </button>
        </div>

        <div v-else-if="credentials" class="space-y-5">
          <div
            class="rounded-lg border border-amber-6 bg-amber-3 px-4 py-3 text-sm text-amber-12"
          >
            {{ tr('WARNING') }}
          </div>

          <section class="rounded-xl border border-n-weak p-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 class="font-medium text-n-slate-12">
                  {{ tr('INBOUND_TITLE') }}
                </h3>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ tr('INBOUND_DESCRIPTION') }}
                </p>
                <p
                  data-testid="inbound-signature-version"
                  class="mt-2 text-xs font-medium text-n-slate-11"
                >
                  {{
                    tr('SIGNATURE_VERSION', {
                      version: credentials.inbound_signature_version,
                    })
                  }}
                </p>
                <p
                  data-testid="inbound-replay-status"
                  class="mt-1 text-xs"
                  :class="
                    credentials.inbound_replay_protection_enabled
                      ? 'text-teal-11'
                      : 'text-amber-11'
                  "
                >
                  {{
                    credentials.inbound_replay_protection_enabled
                      ? tr('REPLAY_ENABLED')
                      : tr('REPLAY_DISABLED')
                  }}
                </p>
              </div>
              <span
                class="rounded-full px-2 py-1 text-xs font-medium"
                :class="
                  credentials.inbound_configured
                    ? 'bg-teal-3 text-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                {{
                  credentials.inbound_configured
                    ? tr('CONFIGURED')
                    : tr('NOT_CONFIGURED')
                }}
              </span>
            </div>

            <div v-if="credentials.inbound_configured" class="mt-4 space-y-3">
              <div>
                <label
                  for="sequence-webhook-inbound-url"
                  class="text-xs font-medium text-n-slate-11"
                >
                  {{ tr('INBOUND_URL') }}
                </label>
                <div class="mt-1 flex gap-2">
                  <input
                    id="sequence-webhook-inbound-url"
                    :value="credentials.inbound_url"
                    type="url"
                    readonly
                    spellcheck="false"
                    class="min-h-11 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 font-mono text-xs text-n-slate-11"
                  />
                  <button
                    type="button"
                    class="min-h-11 min-w-11 rounded-lg border border-n-weak px-3 text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-label="tr('COPY_URL')"
                    @click="copyCredential(credentials.inbound_url)"
                  >
                    <FluentIcon icon="copy" size="16" />
                  </button>
                </div>
              </div>

              <div>
                <label
                  for="sequence-webhook-inbound-secret"
                  class="text-xs font-medium text-n-slate-11"
                >
                  {{ tr('INBOUND_SECRET') }}
                </label>
                <div class="mt-1 flex gap-2">
                  <input
                    id="sequence-webhook-inbound-secret"
                    :value="credentials.inbound_secret"
                    :type="showInboundSecret ? 'text' : 'password'"
                    readonly
                    spellcheck="false"
                    autocomplete="off"
                    class="min-h-11 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 font-mono text-xs text-n-slate-11"
                  />
                  <button
                    type="button"
                    class="min-h-11 min-w-11 rounded-lg border border-n-weak px-3 text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-label="
                      showInboundSecret ? tr('HIDE_SECRET') : tr('SHOW_SECRET')
                    "
                    @click="showInboundSecret = !showInboundSecret"
                  >
                    <FluentIcon
                      :icon="showInboundSecret ? 'eye-hide' : 'eye-show'"
                      size="16"
                    />
                  </button>
                  <button
                    type="button"
                    class="min-h-11 min-w-11 rounded-lg border border-n-weak px-3 text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-label="tr('COPY_SECRET')"
                    @click="copyCredential(credentials.inbound_secret)"
                  >
                    <FluentIcon icon="copy" size="16" />
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="mt-4 min-h-11 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
              :disabled="isRotating || isConfirmingRotation"
              @click="rotateCredentials('inbound')"
            >
              {{
                credentials.inbound_configured
                  ? tr('ROTATE_INBOUND')
                  : tr('GENERATE_INBOUND')
              }}
            </button>
          </section>

          <section class="rounded-xl border border-n-weak p-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 class="font-medium text-n-slate-12">
                  {{ tr('OUTBOUND_TITLE') }}
                </h3>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{ tr('OUTBOUND_DESCRIPTION') }}
                </p>
              </div>
              <span
                class="rounded-full px-2 py-1 text-xs font-medium"
                :class="
                  credentials.outbound_configured
                    ? 'bg-teal-3 text-teal-11'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                {{
                  credentials.outbound_configured
                    ? tr('CONFIGURED')
                    : tr('NOT_CONFIGURED')
                }}
              </span>
            </div>

            <div v-if="credentials.outbound_configured" class="mt-4">
              <label
                for="sequence-webhook-outbound-secret"
                class="text-xs font-medium text-n-slate-11"
              >
                {{ tr('OUTBOUND_SECRET') }}
              </label>
              <div class="mt-1 flex gap-2">
                <input
                  id="sequence-webhook-outbound-secret"
                  :value="credentials.outbound_signing_secret"
                  :type="showOutboundSecret ? 'text' : 'password'"
                  readonly
                  spellcheck="false"
                  autocomplete="off"
                  class="min-h-11 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 font-mono text-xs text-n-slate-11"
                />
                <button
                  type="button"
                  class="min-h-11 min-w-11 rounded-lg border border-n-weak px-3 text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :aria-label="
                    showOutboundSecret ? tr('HIDE_SECRET') : tr('SHOW_SECRET')
                  "
                  @click="showOutboundSecret = !showOutboundSecret"
                >
                  <FluentIcon
                    :icon="showOutboundSecret ? 'eye-hide' : 'eye-show'"
                    size="16"
                  />
                </button>
                <button
                  type="button"
                  class="min-h-11 min-w-11 rounded-lg border border-n-weak px-3 text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :aria-label="tr('COPY_SECRET')"
                  @click="copyCredential(credentials.outbound_signing_secret)"
                >
                  <FluentIcon icon="copy" size="16" />
                </button>
              </div>
            </div>

            <button
              type="button"
              class="mt-4 min-h-11 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
              :disabled="isRotating || isConfirmingRotation"
              @click="rotateCredentials('outbound')"
            >
              {{
                credentials.outbound_configured
                  ? tr('ROTATE_OUTBOUND')
                  : tr('GENERATE_OUTBOUND')
              }}
            </button>

            <div
              class="mt-4 rounded-lg bg-n-alpha-1 p-3 text-xs text-n-slate-11"
            >
              <p>{{ tr('OUTBOUND_SIGNATURE_DESCRIPTION') }}</p>
              <code
                class="mt-2 block overflow-x-auto rounded bg-n-solid-2 p-2 font-mono"
              >
                {{ outboundCanonicalPayload }}
              </code>
            </div>
          </section>

          <section class="rounded-xl bg-n-alpha-1 p-4 text-xs text-n-slate-11">
            <h3 class="font-medium text-n-slate-12">
              {{ tr('SIGNATURE_TITLE') }}
            </h3>
            <p class="mt-2">{{ tr('LEGACY_SIGNATURE_DESCRIPTION') }}</p>
            <code
              class="mt-2 block overflow-x-auto rounded bg-n-solid-2 p-2 font-mono"
            >
              {{ legacyInboundCanonicalPayload }}
            </code>
            <div v-if="usesInboundV2" class="mt-3 border-t border-n-weak pt-3">
              <p>{{ tr('V2_SIGNATURE_DESCRIPTION') }}</p>
              <code
                class="mt-2 block overflow-x-auto rounded bg-n-solid-2 p-2 font-mono"
              >
                {{ v2InboundCanonicalPayload }}
              </code>
              <p class="mt-2">{{ tr('V2_DUAL_SIGNATURE_NOTICE') }}</p>
              <p class="mt-2">
                {{
                  tr('SIGNATURE_WINDOW', {
                    seconds: credentials.authentication_window_seconds,
                  })
                }}
              </p>
            </div>
            <p v-else class="mt-3 text-amber-11">
              {{ tr('V1_REPLAY_NOTICE') }}
            </p>
          </section>

          <button
            v-if="!hasAnyCredential"
            type="button"
            class="min-h-11 w-full rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white hover:bg-n-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
            :disabled="isRotating || isConfirmingRotation"
            @click="rotateCredentials('all')"
          >
            {{ tr('GENERATE_ALL') }}
          </button>
        </div>
      </div>

      <footer class="flex justify-end border-t border-n-weak px-6 py-4">
        <button
          type="button"
          class="min-h-11 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="close"
        >
          {{ tr('CLOSE') }}
        </button>
      </footer>
    </div>

    <ConfirmationModal
      ref="rotationConfirmation"
      :title="tr('ROTATE_TITLE')"
      :description="tr('ROTATE_CONFIRM')"
      :confirm-label="tr('ROTATE_ACTION')"
      :cancel-label="t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_CANCEL')"
    />
  </Modal>
</template>
